import { useEffect, useMemo, useState } from 'react';

import { ReviewerRequest } from './ReviewerRequest';
import { ReviewSheet, type SubmitState } from './ReviewSheet';
import { SectionIdentity } from './SectionIdentity';
import { SectionIndex, SectionProgressBar } from './SectionIndex';
import { SectionObjective } from './SectionObjective';
import { SectionReview } from './SectionReview';
import { SectionSchedule } from './SectionSchedule';
import { SectionSupport } from './SectionSupport';
import { useSectionSpy } from './useSectionSpy';
import { scheduleIssues } from '../../lib/schedule';
import {
  FORM_SECTIONS,
  projectSubmitSchema,
  type ProjectFormValues,
} from '../../schemas/project.schema';
import type {
  CatalogOption,
  Id,
  MinistryMember,
  SpaceOption,
  SupportTypeOption,
  TimelineTransitionEntry,
  WritableMinistry,
} from '@/shared/types/domain';

interface ProjectFormDocumentProps {
  values: ProjectFormValues;
  ministries: WritableMinistry[];
  members: MinistryMember[];
  spaces: SpaceOption[];
  categories: CatalogOption[];
  supportTypes: SupportTypeOption[];
  fundingSources: CatalogOption[];
  projectId?: Id;
  ministryLocked: boolean;
  /** O dia sugerido pelo menu de contexto do Calendário (decisão 75). */
  suggestedDate?: string;
  /** Onde o envio está — governa o botão da folha e o estado "enviado". */
  submitState?: SubmitState;
  /** Falha do envio. Aparece DENTRO da folha, junto do botão que a causou. */
  submitError?: unknown;
  /**
   * **Modo ajuste** (spec-project-adjustments.md §4.1): o projeto voltou em
   * `changes_requested` e o líder está corrigindo uma rodada, não escrevendo um
   * projeto novo. A tela é a mesma; o que muda é a moldura — o verbo do botão,
   * a folha, e o pedido do revisor logo abaixo do índice.
   */
  isAdjusting?: boolean;
  /**
   * O último `request_changes` da linha do tempo (decisão 45). Nulo em tudo que
   * não seja modo ajuste — e nulo também na 1ª rodada de um projeto que nunca
   * foi devolvido.
   */
  reviewerRequest?: TimelineTransitionEntry | null;
  onChangeText: (patch: Partial<ProjectFormValues>) => void;
  onChangeNow: (patch: Partial<ProjectFormValues>) => void;
  /** `comment` é a observação de reenvio (decisão 47) — nula no envio inicial. */
  onSubmit: (comment: string | null) => void;
}

const SECTION_IDS = FORM_SECTIONS.map((section) => section.id);

/**
 * O formulário inteiro: **cinco seções, uma tela, uma rolagem** (decisão 21).
 *
 * Não existe `next`/`back`, nem estado de "etapa atual" governando o que está
 * montado — as cinco seções estão todas no DOM ao mesmo tempo, e navegar é
 * rolar. Preencher um projeto é escrever um documento: a pessoa precisa ver o
 * tamanho do que está preenchendo, voltar duas seções para conferir a data
 * enquanto escreve o orçamento, e chegar ao fim sabendo o que respondeu.
 *
 * O que a spec chama de "indicação clara de progresso" (US-15) é o índice
 * lateral em `lg+` e a barra fixa abaixo disso — progresso é quanto do
 * documento está pronto, não em que passo você está.
 *
 * **A única quebra dessa regra é a folha de revisão**, e ela não é uma etapa:
 * é a confirmação de uma ação irreversível, que só aparece depois que o
 * documento inteiro já está válido. Nada se preenche lá dentro — só se lê e se
 * decide.
 */
export function ProjectFormDocument({
  values,
  ministries,
  members,
  spaces,
  categories,
  supportTypes,
  fundingSources,
  projectId,
  ministryLocked,
  suggestedDate,
  submitState = 'idle',
  submitError,
  isAdjusting = false,
  reviewerRequest = null,
  onChangeText,
  onChangeNow,
  onSubmit,
}: ProjectFormDocumentProps) {
  const activeId = useSectionSpy(SECTION_IDS);
  const [showErrors, setShowErrors] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  /**
   * A observação de reenvio (decisão 47) mora AQUI, e não na folha: a folha
   * desmonta ao fechar, e "Voltar e ajustar" não pode apagar o que a pessoa
   * escreveu. Sair da tela descarta — nada de `localStorage` (regra 7).
   */
  const [resubmitComment, setResubmitComment] = useState('');

  const validation = useMemo(() => projectSubmitSchema.safeParse(values), [values]);

  /**
   * Os erros por campo, e o que falta em linguagem de voluntário.
   *
   * Só aparecem depois de uma tentativa de envio (`showErrors`): acusar
   * "obrigatório" em campo que a pessoa ainda nem chegou é o formulário
   * brigando com quem o preenche.
   */
  const { fieldErrors, missing } = useMemo(() => {
    if (validation.success) return { fieldErrors: {}, missing: [] };

    const errors: Partial<Record<keyof ProjectFormValues, string>> = {};
    const pending: { sectionId: string; label: string }[] = [];

    for (const issue of validation.error.issues) {
      const field = issue.path[0] as keyof ProjectFormValues | undefined;
      if (!field) continue;
      if (!errors[field]) errors[field] = issue.message;

      const section = FORM_SECTIONS.find((entry) =>
        entry.requiredFields.includes(field),
      );
      const sectionId = section?.id ?? FORM_SECTIONS[FORM_SECTIONS.length - 1]!.id;
      if (!pending.some((item) => item.label === issue.message)) {
        pending.push({ sectionId, label: issue.message });
      }
    }

    return { fieldErrors: errors, missing: pending };
  }, [validation]);

  /**
   * A regra de data é a exceção do `showErrors`, e por um motivo:
   * "falta preencher" é sobre o que a pessoa AINDA NÃO fez, mas 11/10 às 20h
   * terminando 11/10 às 18h é contradição entre duas coisas que ela já
   * escreveu. Guardar isso até o botão do fim do documento faria a pessoa
   * rolar cinco seções para descobrir um erro que estava na tela no instante
   * em que ela o cometeu.
   */
  const scheduleErrors = useMemo(
    () => scheduleIssues(values.starts_at, values.ends_at),
    [values.starts_at, values.ends_at],
  );

  const visibleErrors = { ...(showErrors ? fieldErrors : {}), ...scheduleErrors };

  /**
   * O documento parou de ser válido com a folha aberta — alguém apagou um
   * campo em outra aba, ou o rascunho voltou do servidor sem ele. A folha
   * fecha: confirmar o envio de um projeto que o botão de fora já não
   * liberaria é a definição de janela desatualizada.
   */
  useEffect(() => {
    if (isReviewOpen && !validation.success && submitState === 'idle') setIsReviewOpen(false);
  }, [isReviewOpen, validation.success, submitState]);

  function handleReview() {
    if (!validation.success) {
      setShowErrors(true);
      return;
    }
    setIsReviewOpen(true);
  }

  return (
    <>
      <SectionProgressBar values={values} activeId={activeId} />

      <div className="lg:grid lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-10">
        <SectionIndex
          values={values}
          activeId={activeId}
          reviewerRequest={
            reviewerRequest ? <ReviewerRequest entry={reviewerRequest} /> : undefined
          }
        />

        {/* Um `<form>` só, e `max-w-3xl`: campo em uma coluna, largura de
            leitura. Formulário largo obriga o olho a varrer horizontalmente e
            mata a leitura de documento. */}
        <form
          className="max-w-3xl space-y-10 pt-6 lg:pt-0"
          onSubmit={(event) => {
            event.preventDefault();
            handleReview();
          }}
        >
          {/* **Abaixo de `lg` o pedido é o PRIMEIRO bloco do documento**
              (decisão 46), acima da seção 1. A coluna do índice é
              `hidden lg:block`, então deixá-lo só lá o faria desaparecer
              exatamente onde ele é mais necessário — e ele é a informação mais
              importante da tela: não pode ser o que some. Colapsável ali
              porque, em 375px, cada linha empurra o primeiro campo para fora
              da dobra. */}
          {reviewerRequest ? (
            <ReviewerRequest entry={reviewerRequest} collapsible className="lg:hidden" />
          ) : null}

          <SectionIdentity
            values={values}
            ministries={ministries}
            categories={categories}
            errors={visibleErrors}
            onChangeText={onChangeText}
            onChangeNow={onChangeNow}
            ministryLocked={ministryLocked}
          />

          <SectionObjective
            values={values}
            errors={visibleErrors}
            onChangeText={onChangeText}
          />

          <SectionSchedule
            values={values}
            spaces={spaces}
            errors={visibleErrors}
            onChangeNow={onChangeNow}
            projectId={projectId}
            suggestedDate={suggestedDate}
          />

          <SectionSupport
            values={values}
            supportTypes={supportTypes}
            fundingSources={fundingSources}
            onChangeText={onChangeText}
            onChangeNow={onChangeNow}
          />

          <SectionReview
            values={values}
            members={members}
            errors={visibleErrors}
            missing={showErrors ? missing : []}
            canSubmit={validation.success}
            isSubmitting={submitState !== 'idle'}
            isAdjusting={isAdjusting}
            dateIssue={scheduleErrors.starts_at ?? scheduleErrors.ends_at ?? null}
            onChangeText={onChangeText}
            onChangeNow={onChangeNow}
            onReview={handleReview}
          />
        </form>
      </div>

      <ReviewSheet
        open={isReviewOpen}
        onClose={() => setIsReviewOpen(false)}
        // `null` é a ausência de observação em todas as camadas — a folha nunca
        // entrega string em branco para a RPC decidir o que fazer com ela.
        onConfirm={() => onSubmit(isAdjusting ? resubmitComment.trim() || null : null)}
        state={submitState}
        error={submitError}
        values={values}
        ministries={ministries}
        members={members}
        spaces={spaces}
        categories={categories}
        supportTypes={supportTypes}
        fundingSources={fundingSources}
        isAdjusting={isAdjusting}
        resubmitComment={resubmitComment}
        onResubmitCommentChange={setResubmitComment}
      />
    </>
  );
}
